/**
 * Landing-page variants for paid traffic (Google Ads / Meta / TikTok).
 *
 * A visitor arriving with a matching `utm_campaign` (or explicit `?variant=`
 * query param) sees a tailored hero kicker, body copy and primary CTA in
 * both English and Arabic. Everything else on the page stays identical —
 * we're only shifting the message that greets the visitor.
 *
 * Matching is case-insensitive substring on `utm_campaign` so any variation
 * of a campaign name (e.g. "GoldenVisa-Q3-KSA", "golden_visa_v2") maps
 * to the correct variant.
 */

export const AD_VARIANTS = {
  "golden-visa": {
    match: ["golden", "visa", "residency"],
    en: {
      kicker: "10-year UAE Golden Visa pathway",
      body: "Own the right Dubai property and step onto the 10-year UAE Golden Visa pathway for you and your family. AED 2M+ property value is one eligibility criterion under Cabinet Decision 65/2022; final issuance is subject to the current ICP/DLD checklist. As a RERA-licensed advisor we walk you through the published rules, no shortcuts, no surprises.",
      cta_primary: "Check my eligibility",
    },
    ar: {
      kicker: "الإقامة الذهبية لعشر سنوات",
      body: "تملّك العقار المناسب في دبي وافتح لعائلتك الإقامة الذهبية لعشر سنوات. بصفتنا وسيطاً مرخّصاً من دائرة الأراضي، نوضّح لك شروط الأهلية الرسمية من الهيئة الاتحادية للهوية والجنسية دون أي تعقيد.",
      cta_primary: "تحقّق من أهليتك",
    },
  },

  "mortgage": {
    match: ["mortgage", "loan", "financing", "finance"],
    en: {
      kicker: "Non-resident mortgage advisory",
      body: "Buying Dubai property with a mortgage, even as a non-resident. We compare CBUAE-regulated banks, structure your down-payment, and lock the term rate that works for your income profile.",
      cta_primary: "Get my mortgage plan",
    },
    ar: {
      kicker: "استشارات الرهن العقاري للمستثمرين",
      body: "اشتري عقارك في دبي عبر تمويل مصرفي, حتى من خارج الإمارات. نقارن لك البنوك المرخّصة من المصرف المركزي، ونصمّم دفعتك الأولى ونؤمّن لك أفضل معدّل يناسب دخلك.",
      cta_primary: "احصل على خطتك التمويلية",
    },
  },

  "off-plan": {
    match: ["off-plan", "offplan", "off_plan", "new-launch", "launch"],
    en: {
      kicker: "Off-plan Dubai · vetted only",
      body: "Skip the noise. We shortlist off-plan launches from developers with a proven RERA delivery record, sold at the same price you'd pay direct, but with an independent advisor reviewing every clause.",
      cta_primary: "See vetted launches",
    },
    ar: {
      kicker: "قيد الإنشاء · مشاريع مختارة",
      body: "لا مزيد من الضجيج. نختار لك مشاريع قيد الإنشاء من مطوّرين لديهم سجل تسليم موثّق لدى دائرة الأراضي, بنفس سعر المطوّر مع مستشار مستقل يراجع كل بند.",
      cta_primary: "استعرض المشاريع المختارة",
    },
  },

  "investment": {
    match: ["invest", "roi", "yield", "rental"],
    en: {
      kicker: "Investor-grade Dubai property",
      body: "We build your Dubai portfolio around one number: net yield you actually keep. Every unit we recommend is benchmarked against DLD transaction data and asking rents from online property portal listings, never fabricated projections.",
      cta_primary: "Book my strategy call",
    },
    ar: {
      kicker: "عقار دبي بمنظور المستثمر",
      body: "نبني محفظتك في دبي حول رقم واحد: العائد الصافي الفعلي بعد المصروفات. كل وحدة نرشّحها تُقارَن ببيانات دائرة الأراضي وأسعار الطلب من إعلانات بوابات العقارات الإلكترونية, بلا توقّعات وهميّة.",
      cta_primary: "احجز مكالمتك الاستراتيجية",
    },
  },
};

/**
 * Return a variant key based on the current URL search params.
 * Priority: explicit `?variant=` > utm_campaign match > null.
 */
export function pickVariant(searchParams) {
  if (!searchParams) return null;
  const explicit = (searchParams.get("variant") || "").toLowerCase().trim();
  if (explicit && AD_VARIANTS[explicit]) return explicit;

  const utm = (searchParams.get("utm_campaign") || "").toLowerCase().trim();
  if (!utm) return null;

  for (const [key, def] of Object.entries(AD_VARIANTS)) {
    if (def.match.some((needle) => utm.includes(needle))) return key;
  }
  return null;
}
